import React from 'react';
import { Download, Linkedin, Mail, MapPin, Phone } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Logo from './Logo';
import FooterHeroBackground, { FooterNavLink } from './FooterHeroBackground';
import cvFile from '../assets/TALLA_FOSSI_Florent_Martial_CV.pdf';
import { useLocale } from '../context/LocaleContext';
import { HOME_CONTACT_SECTION_ID, HOME_NAV_SECTIONS, homeSectionHref } from '../config/homeNav';
import { scrollToSection } from '../utils/scrollToSection';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLocale();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleSectionClick = (sectionId) => (event) => {
    if (isHome) {
      event.preventDefault();
      scrollToSection(sectionId);
    }
  };

  const handleContactClick = (event) => {
    if (isHome) {
      event.preventDefault();
      scrollToSection(HOME_CONTACT_SECTION_ID);
    }
  };

  const isActive = (sectionId) => isHome && location.hash === `#${sectionId}`;

  return (
    <footer className="rihen-footer">
      <FooterHeroBackground />
      <div className="rihen-footer__scrim" aria-hidden="true" />

      <div className="rihen-site-inner rihen-footer__inner">
        <div className="rihen-footer__grid" data-reveal-stagger>
          <div className="rihen-footer__col rihen-footer__col--brand" data-reveal="fade">
            <div className="rihen-footer__brand-rail" aria-hidden="true">
              <span className="rihen-footer__brand-rail-dot" />
            </div>
            <div className="rihen-footer__brand-body">
              <Logo imgClassName="rihen-brand-logo rihen-footer__logo" />
              <p className="rihen-footer__tagline">{t('foot_tagline')}</p>
              <a
                href="https://www.linkedin.com/in/florent-martial-talla-fossi-291ab8172"
                target="_blank"
                rel="noopener noreferrer"
                className="rihen-footer__social"
                title="LinkedIn"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <div className="rihen-footer__col" data-reveal="fade">
            <h4 className="rihen-footer__title">{t('foot_nav')}</h4>
            <ul className="rihen-footer__list">
              <li>
                <FooterNavLink href="/">{t('foot_home')}</FooterNavLink>
              </li>
              {HOME_NAV_SECTIONS.map((item) => (
                <li key={item.id}>
                  <FooterNavLink
                    href={homeSectionHref(item.id)}
                    onClick={handleSectionClick(item.id)}
                    className={isActive(item.id) ? 'active' : ''}
                  >
                    {t(item.labelKey)}
                  </FooterNavLink>
                </li>
              ))}
              <li>
                <FooterNavLink
                  href={homeSectionHref(HOME_CONTACT_SECTION_ID)}
                  onClick={handleContactClick}
                  className={isActive(HOME_CONTACT_SECTION_ID) ? 'active' : ''}
                >
                  {t('foot_contact')}
                </FooterNavLink>
              </li>
            </ul>
          </div>

          <div className="rihen-footer__col" data-reveal="fade">
            <h4 className="rihen-footer__title">{t('foot_contact')}</h4>
            <ul className="rihen-footer__contact">
              <li>
                <MapPin size={16} aria-hidden="true" />
                <span>Yaoundé, Cameroun</span>
              </li>
              <li>
                <Phone size={16} aria-hidden="true" />
                <div className="rihen-footer__phones">
                  <a href="https://wa.me/237693690308" target="_blank" rel="noopener noreferrer">
                    (+237) 693690308
                  </a>
                  <a href="https://wa.me/237653742082" target="_blank" rel="noopener noreferrer">
                    (+237) 653742082
                  </a>
                </div>
              </li>
              <li>
                <Mail size={16} aria-hidden="true" />
                <a href="mailto:fossiflorent@gmail.com">fossiflorent@gmail.com</a>
              </li>
            </ul>
          </div>

          <div className="rihen-footer__col" data-reveal="fade">
            <h4 className="rihen-footer__title">{t('foot_cv_title')}</h4>
            <p className="rihen-footer__cv-text">{t('foot_cv_text')}</p>
            <a
              href={cvFile}
              download="TALLA_FOSSI_Florent_Martial_CV.pdf"
              className="rihen-btn rihen-btn-teal interactive-button inline-flex items-center"
            >
              <Download size={16} />
              {t('foot_cv_btn')}
            </a>
          </div>
        </div>

        <div className="rihen-footer__divider">
          <span className="rihen-footer__divider-line" aria-hidden="true" />
          <p>
            © {currentYear} TALLA FOSSI Florent Martial. {t('foot_rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
